import React, { useState } from "react";
import Chart from "react-apexcharts";
import {
  Box,
  Button,
  ButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Aggregates mensuration data for all measurement fields (excluding the "datemensuration" field).
const aggregateMensurationData = (rawData, viewMode) => {
  if (!rawData.length) return [];

  // Identify measurement fields (exclude "datemensuration", "mensurationID", "clientid")
  const measurementKeys = Object.keys(rawData[0]).filter(
    (key) =>
      key !== "datemensuration" && key !== "mensurationID" && key !== "clientid"
  );

  return measurementKeys.map((field) => {
    let groups = {};
    rawData.forEach((item) => {
      const dateObj = new Date(item.datemensuration);
      if (isNaN(dateObj.getTime())) return; // Skip invalid dates

      let groupKey;
      if (viewMode === "days") {
        // Use ISO date string: YYYY-MM-DD
        groupKey = dateObj.toISOString().split("T")[0];
      } else if (viewMode === "months") {
        // e.g. "Feb 2025"
        groupKey = dateObj.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      } else if (viewMode === "years") {
        // e.g. "2025"
        groupKey = dateObj.getFullYear().toString();
      }
      if (!groups[groupKey]) {
        groups[groupKey] = { sum: 0, count: 0 };
      }
      groups[groupKey].sum += item[field];
      groups[groupKey].count += 1;
    });

    // Build the series data for the field.
    const data = Object.entries(groups).map(([groupKey, { sum, count }]) => ({
      x: groupKey,
      y: sum / count,
    }));

    // Sort data chronologically.
    data.sort((a, b) => new Date(a.x) - new Date(b.x));

    return {
      name: field,
      data,
    };
  });
};

const TimeSeriesMensurationChart = ({ rawData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [viewMode, setViewMode] = useState("days");

  // Aggregate data for all measurement fields.
  const series = aggregateMensurationData(rawData, viewMode);

  // Configure x-axis: if days, use datetime; otherwise, use category.
  const xaxisOptions =
    viewMode === "days"
      ? {
          type: "datetime",
          labels: {
            format: "dd MMM",
            style: {
              fontSize: isMobile ? "10px" : "12px",
              colors: ["#000"],
            },
          },
        }
      : {
          type: "category",
          labels: {
            style: {
              fontSize: isMobile ? "10px" : "12px",
              colors: ["#000"],
            },
          },
        };

  // ApexCharts configuration options.
  const options = {
    chart: {
      type: "line",
      zoom: { enabled: true },
      toolbar: { show: true },
      animations: { enabled: true },
      foreColor: "#000",
    },
    stroke: { curve: "smooth" },
    dataLabels: {
      enabled: true,
      style: { fontSize: isMobile ? "10px" : "12px", colors: ["#000"] },
    },
    xaxis: {
      ...xaxisOptions,
      title: {
        text: "Date",
        style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
      },
    },
    yaxis: {
      title: {
        text: "Measurement Value",
        style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
      },
      labels: {
        style: { fontSize: isMobile ? "10px" : "12px", colors: ["#000"] },
      },
    },
    tooltip: {
      theme: "light",
      style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
    },
    legend: {
      position: isMobile ? "bottom" : "right",
      horizontalAlign: isMobile ? "center" : "left",
      labels: { colors: ["#000"], fontSize: isMobile ? "10px" : "12px" },
    },
  };

  return (
    <Box>
      <ButtonGroup size="small" variant="outlined" sx={{ mb: 2 }}>
        <Button onClick={() => setViewMode("days")}>Days</Button>
        <Button onClick={() => setViewMode("months")}>Months</Button>
        <Button onClick={() => setViewMode("years")}>Years</Button>
      </ButtonGroup>
      {/* The key prop forces reinitialization when viewMode changes */}
      <Chart
        key={viewMode}
        options={options}
        series={series}
        type="line"
        height="400"
      />
    </Box>
  );
};

export default TimeSeriesMensurationChart;
